export const HLSVideo = {
  videoURL() {
    return this.el.dataset.url;
  },

  mounted() {
    this.previousStartTime = null;
    this.initPlayer();

    this.el.addEventListener("set-video-time", (event) => {
      if (this.player) {
        const time = event.detail.time;
        console.log(`Setting video time to ${time}s`);
        this.player.currentTime = Math.max(0, time);
      }
    });

    this.el.addEventListener("scroll-to-video", () => {
      console.log("Scrolling to video player");
      this.el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  },

  updated() {
    console.log(`Updated HLS video: ${this.videoURL()}`);

    // Check if URL changed and update source
    const currentURL = this.videoURL();
    if (this.player && this.currentURL !== currentURL) {
      // A new recording URL resets the decode-skip budget.
      this.decodeSkips = 0;
      this.lastDecodeSkipTarget = null;
      this.setSrc(currentURL);
    }

    // Check if start-time changed and seek accordingly
    this.handleStartTimeChange();
  },

  handleStartTimeChange() {
    if (!this.player) return;

    const startTimeAttr = this.player.getAttribute("start-time");
    const currentStartTime = this.parseStartTime(startTimeAttr);

    if (currentStartTime !== this.previousStartTime) {
      console.log(`Seeking to ${currentStartTime}s`);
      this.player.currentTime = Math.max(0, currentStartTime);
    }

    this.previousStartTime = currentStartTime;
  },

  async initPlayer() {
    await import("@mux/mux-player");

    const url = this.videoURL();
    console.log(`Initializing Mux video: ${url}`);

    if (!url) {
      console.warn(
        "HLSVideo: data-url is empty; no recording URL to play. The player will render blank.",
      );
    }

    this.player = this.el.querySelector("mux-player");
    if (!this.player) {
      console.warn("HLSVideo: <mux-player> element not found; player did not initialize.");
      return;
    }

    this.currentURL = url;

    this.player.muted = true;
    this.setSrc(url);

    // Initialize start time tracking
    const startTimeAttr = this.player.getAttribute("start-time");
    this.previousStartTime = this.parseStartTime(startTimeAttr);

    // Recovery from decode failures on individual .ts segments. A segment
    // carrying no SPS/PPS decodes to nothing and throws a MediaError; seeking
    // past it lets playback continue.
    //
    // Recordings written before sidecars emitted codec config ahead of every
    // IDR have roughly every other segment in that state, so the budget counts
    // *consecutive* failures and resets whenever playback demonstrably moves
    // forward. A fixed lifetime total would strand such a stream a few
    // segments in, however large the total was.
    this.decodeSkips = 0;
    this.maxConsecutiveDecodeSkips = 5;
    // .ts segments are ~3s; skip 4s to land safely in the next segment.
    this.decodeSkipSeconds = 4;
    // Where the last skip aimed, and how far past it playback must reach for
    // the stream to count as progressing rather than stuck in one spot.
    this.lastDecodeSkipTarget = null;
    this.decodeProgressSeconds = 1;

    this.player.addEventListener("error", (event) => {
      const error = event.detail || {};

      // Structured error log so support can capture the failure from the
      // browser console on the next occurrence.
      console.log("Mux Player Error:", {
        name: error.name,
        code: error.code,
        message: error.message,
        src: this.currentURL,
        currentTime: this.player.currentTime,
        networkState: this.player.networkState,
        readyState: this.player.readyState,
        decodeSkips: this.decodeSkips,
      });

      this.recoverFromDecodeError(error);
    });
  },

  // Set the player source, guarding against the element not having upgraded
  // to the mux-player custom element yet (which would drop the assignment).
  setSrc(url) {
    if (!this.player) return;
    this.currentURL = url;

    // Assign immediately; if the mux-player element has not upgraded yet the
    // assignment can be lost, so re-apply once the custom element is defined.
    this.player.src = url;

    if (window.customElements && window.customElements.whenDefined) {
      window.customElements
        .whenDefined("mux-player")
        .then(() => {
          this.player.src = url;
        })
        .catch((error) => {
          console.warn(
            `HLSVideo: mux-player custom element never defined; src may not have applied for ${url}.`,
            error,
          );
        });
    }
  },

  // On a decode/MediaError, seek forward a small amount to skip the bad
  // segment. Uses the real mux-player `currentTime` API, and bounds
  // *consecutive* failures so it can never loop in place.
  recoverFromDecodeError(error) {
    if (error.name !== "MediaError") return;

    const current = this.player.currentTime;
    const from = Number.isFinite(current) ? current : 0;

    // Playback reached past where the last skip landed, so the stream is
    // advancing and earns a fresh budget. Every reset costs at least
    // decodeProgressSeconds of real playback, so recovery still terminates on
    // a finite recording.
    if (
      this.lastDecodeSkipTarget !== null &&
      from > this.lastDecodeSkipTarget + this.decodeProgressSeconds
    ) {
      this.decodeSkips = 0;
    }

    if (this.decodeSkips >= this.maxConsecutiveDecodeSkips) {
      console.warn(
        `HLSVideo: giving up after ${this.decodeSkips} consecutive decode-error skips without progress; leaving the player error UI visible.`,
      );
      return;
    }

    const target = from + this.decodeSkipSeconds;

    this.decodeSkips += 1;
    this.lastDecodeSkipTarget = target;
    console.warn(
      `HLSVideo: skipping bad .ts segment (attempt ${this.decodeSkips}/${this.maxConsecutiveDecodeSkips}); seeking ${from}s -> ${target}s`,
    );

    this.player.currentTime = target;
    this.player.play?.().catch(() => {});
  },

  parseStartTime(startTimeAttr) {
    if (!startTimeAttr) return 0;
    const parsed = parseFloat(startTimeAttr);
    return isNaN(parsed) ? 0 : parsed;
  },
};
